import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  usePendingTimebankRequests,
  useDecideTimebankRequest,
  type TimebankTransaction,
} from "@/hooks/use-timebank";
import { useHighlight } from "@/hooks/use-highlight";
import { formatDate, formatHours } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { AllTransactionsAdminTable } from "@/components/timebank/AllTransactionsAdminTable";
import { TimebankRequestContextLine } from "@/components/timebank/TimebankRequestContextLine";
import { TeamAbsenceFollowUp } from "@/components/timebank/TeamAbsenceFollowUp";

export default function TimebankApprovalsPage() {
  const { activeUser } = useImpersonation();
  // VIB-2766: `decided_by` er aktøren på en ØKONOMIBESLUTNING, og skal derfor
  // være den FAKTISK innloggede brukeren, ikke den impersonerte. CLAUDE.md er
  // eksplisitt om dette, og samme mønster brukes i useCreateAbsence. Tilgangen
  // til siden gates fortsatt på `activeUser`, så admin kan se flaten slik en
  // annen rolle ser den — men beslutningen signeres av den som faktisk tok den.
  const { user: selfUser } = useAuth();
  const isAdmin =
    activeUser?.role === "admin" || activeUser?.role === "superadmin";
  const { data: pending = [], isLoading } = usePendingTimebankRequests();
  const decideMut = useDecideTimebankRequest();

  const [rejectingTx, setRejectingTx] = useState<TimebankTransaction | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  if (!isAdmin) {
    return (
      <div className="page-container">
        <p className="text-sm text-muted-foreground">
          Du har ikke tilgang til timebank-godkjenninger.
        </p>
      </div>
    );
  }

  const deposits = pending.filter((t) => t.kind === "deposit");
  const withdrawals = pending.filter((t) => t.kind === "withdrawal");

  const handleApprove = (tx: TimebankTransaction) => {
    if (!selfUser) return;
    decideMut.mutate({
      transaction: tx,
      decision: "approved",
      decided_by: selfUser!.id,
    });
  };

  const handleConfirmReject = () => {
    if (!rejectingTx || !selfUser) return;
    decideMut.mutate(
      {
        transaction: rejectingTx,
        decision: "rejected",
        decided_by: selfUser!.id,
        rejected_reason: rejectReason.trim() || undefined,
      },
      {
        onSuccess: () => {
          setRejectingTx(null);
          setRejectReason("");
        },
      },
    );
  };

  return (
    <div className="page-container animate-fade-in space-y-6">
      <div className="flex items-center gap-2 mb-2">
        <SrIcon name="clock" className="w-6 h-6 text-muted-foreground" />
        <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em]">
          Timebank-godkjenninger
        </h1>
      </div>
      <p className="text-sm text-muted-foreground">
        Søknader om oppsparing til timebank og uttak til avspassering venter på
        din godkjenning.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : pending.length === 0 ? (
        <div className="border-[1.5px] border-foreground bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Ingen søknader venter på godkjenning.
          </p>
        </div>
      ) : (
        <>
          <Section
            title="Oppsparing til timebank"
            transactions={deposits}
            onApprove={handleApprove}
            onReject={setRejectingTx}
            kind="deposit"
            disabled={decideMut.isPending}
          />
          <Section
            title="Uttak (avspassering)"
            transactions={withdrawals}
            onApprove={handleApprove}
            onReject={setRejectingTx}
            kind="withdrawal"
            disabled={decideMut.isPending}
          />
        </>
      )}

      {/* VIB-1653: fravær er oppfølging, ikke godkjenning — egen lese-kun
          seksjon, og landingsplassen for absence_registered-varsler. */}
      <TeamAbsenceFollowUp />

      {/* VIB-726: Admin/superadmin har komplett oversikt + cancel-flyt */}
      <AllTransactionsAdminTable />


      <Dialog
        open={!!rejectingTx}
        onOpenChange={(o) => {
          if (!o) {
            setRejectingTx(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">
              Avslå søknad
            </DialogTitle>
          </DialogHeader>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Begrunnelse (valgfritt)
            </label>
            <textarea
              rows={3}
              className="mt-1 w-full rounded-md border-[1.5px] border-foreground bg-background px-2 py-1.5 text-sm"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Beskriv hvorfor søknaden avslås (sendes som varsel)"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setRejectingTx(null);
                setRejectReason("");
              }}
              disabled={decideMut.isPending}
            >
              Avbryt
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmReject}
              disabled={decideMut.isPending}
            >
              {decideMut.isPending ? (
                <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <SrIcon name="x" className="w-4 h-4 mr-1.5" />
              )}
              Avslå
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({
  title,
  transactions,
  onApprove,
  onReject,
  kind,
  disabled,
}: {
  title: string;
  transactions: TimebankTransaction[];
  onApprove: (tx: TimebankTransaction) => void;
  onReject: (tx: TimebankTransaction) => void;
  kind: "deposit" | "withdrawal";
  disabled: boolean;
}) {
  if (transactions.length === 0) return null;
  return (
    <div className="border-[1.5px] border-foreground bg-card">
      <div className="flex items-center gap-2 p-4 border-b">
        <SrIcon
          name={kind === "deposit" ? "arrow-down" : "arrow-up"}
          className={cn(
            "w-4 h-4",
            kind === "deposit" ? "text-success" : "text-primary",
          )}
        />
        <h3 className="font-display text-lg">{title}</h3>
        <span className="text-xs px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">
          {transactions.length}
        </span>
      </div>
      <ul className="divide-y divide-border/50">
        {transactions.map((tx) => (
          <TimebankRequestRow
            key={tx.id}
            tx={tx}
            kind={kind}
            disabled={disabled}
            onApprove={onApprove}
            onReject={onReject}
          />
        ))}
      </ul>
    </div>
  );
}

function TimebankRequestRow({
  tx,
  kind,
  disabled,
  onApprove,
  onReject,
}: {
  tx: TimebankTransaction;
  kind: "deposit" | "withdrawal";
  disabled: boolean;
  onApprove: (tx: TimebankTransaction) => void;
  onReject: (tx: TimebankTransaction) => void;
}) {
  const ref = useHighlight<HTMLLIElement>(tx.id);
  const taskLabel = tx.assignment?.budget_lines?.description ?? null;
  const projectName = tx.assignment?.budget_lines?.projects?.name ?? null;
  return (
    <li ref={ref} className="px-4 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium">{tx.user?.full_name ?? "Ukjent"}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(tx.created_at)}
                  </span>
                </div>
                {kind === "deposit" ? (
                  <p className="text-xs text-muted-foreground truncate">
                    {taskLabel
                      ? projectName
                        ? `${taskLabel} (${projectName})`
                        : taskLabel
                      : "Oppgave fjernet"}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {tx.start_date && tx.end_date
                      ? `${formatDate(tx.start_date)}${tx.start_time ? ` ${tx.start_time.slice(0, 5)}` : ""} – ${formatDate(tx.end_date)}${tx.end_time ? ` ${tx.end_time.slice(0, 5)}` : ""}`
                      : "Uten datointervall"}
                  </p>
                )}
                {tx.comment && (
                  <p className="text-xs text-foreground/80 mt-1 italic">
                    "{tx.comment}"
                  </p>
                )}
                {/* VIB-1286: kapasitet/fravær-kontekst for godkjenner */}
                <TimebankRequestContextLine tx={tx} />
              </div>
              <span className="text-base tabular-nums font-medium shrink-0">
                {kind === "deposit" ? "+" : "−"}
                {formatHours(tx.hours)}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-green-600 hover:bg-green-50"
                  title="Godkjenn"
                  aria-label="Godkjenn"
                  onClick={() => onApprove(tx)}
                  disabled={disabled}
                >
                  <SrIcon name="check" className="w-4 h-4" />
                </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
        title="Avslå"
        aria-label="Avslå"
        onClick={() => onReject(tx)}
        disabled={disabled}
      >
        <SrIcon name="x" className="w-4 h-4" />
      </Button>
      </div>
    </li>
  );
}
